import { Component, computed, ElementRef, HostListener, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { Wk8BookService } from '@core/wk8/wk8-book.service';
import { Wk8ReadService } from '@core/wk8/wk8-read.service';
import { Wk8TutorPanelComponent } from '@core/wk8/wk8-tutor-panel/wk8-tutor-panel.component';
import { Wk8TutorService } from '@core/wk8/wk8-tutor.service';
import { LpActionsService } from '@features/lp/lp-actions/lp-actions.service';
import { LpMissionService } from '@features/lp/lp-mission/lp-mission.service';
import { DustParticlesComponent } from '@features/particles/dust-particles/dust-particles.component';
import { CAROUSEL_ANIMATION, FADE_ANIMATION, MODAL_ANIMATION, SLIDE_ANIMATION } from '@features/styles/animations';
import { StylesService } from '@features/styles/styles.service';
import { Wk8AudioService } from '@features/wk8/core/wk8-audio/wk8-audio.service';
import { Wk8GlobalsService } from '@features/wk8/core/wk8-globals.service';
import { Wk8WordService } from '@features/wk8/core/wk8-words/wk8-word-service.service';
import { LeesFlitsenVariant } from '@features/wk8/mechanics/wk8-lees/variants/lees-flitsen.variant';
import { ILeesVariant } from '@features/wk8/mechanics/wk8-lees/variants/lees-variant.interface';
import { LeesZinVariant } from '@features/wk8/mechanics/wk8-lees/variants/lees-zin.variant';
import { Wk8LeesComponent } from '@features/wk8/mechanics/wk8-lees/wk8-lees.component';
import { Wk8WisselklankenComponent } from '@features/wk8/mechanics/wk8-wisselklanken/wk8-wisselklanken.component';
import { Wk8BookSentenceBuilderComponent } from '@features/wk8/wk8-book/wk8-book-sentence-builder/wk8-book-sentence-builder.component';
import { waitForMillis } from '@shared/delay.utils';
import { sampleRandom } from '@shared/misc.utils';
import { WordToKlankConverter } from '@shared/woordkr8/WordToKlankConverter';
import { LpWk8BookSentence, LpWk8BookSentencePartFilter, LpWoordData, LpWoordKr8LevelActivity } from '@typings/lp/generated';
import { AdaptivityService } from '../adaptivity.service';
import { MutationTreeBuilder } from '../mechanics/mutation-path/utils/mutation.utils';
import {
  MediaResource,
  QuizConfig,
  QuizOption,
  QuizQuestion,
  Wk8SorterenComponent,
} from '../mechanics/wk8-sorteren/wk8-sorteren.component';
import { Wk8WoordpadComponent } from '../mechanics/wk8-woordpad/wk8-woordpad.component';

const MECHANIC_PRESETS = [
  'wissel_klanken',
  'sorteer_woorden',
  'woordpad',
  'lees_zin',
  'lees_woord',
  'lees_flitsen',
  'lees_flitsenMutaties',
] as const;
type MechanicPreset = (typeof MECHANIC_PRESETS)[number];

interface BaseMechanicInput {
  componentSelector: string;
  arguments: {
    [key: string]: unknown;
  };
}

interface LezenInput extends BaseMechanicInput {
  componentSelector: 'wk8-lees';
  arguments: {
    variant: ILeesVariant;
  };
}

interface WisselInput extends BaseMechanicInput {
  componentSelector: 'wk8-wisselklanken';
  arguments: {
    targetWord: LpWoordData;
  };
}

interface SorteerInput extends BaseMechanicInput {
  componentSelector: 'wk8-sorteren';
  arguments: {
    config?: QuizConfig;
    question: QuizQuestion;
    options: QuizOption[];
  };
}

interface WoordpadInput extends BaseMechanicInput {
  componentSelector: 'wk8-woordpad';
  arguments: {
    path: LpWoordData[];
  };
}

type MechanicInput = LezenInput | WisselInput | SorteerInput | WoordpadInput;

@Component({
  selector: 'wk8-level-activity',
  imports: [
    Wk8BookSentenceBuilderComponent,
    Wk8TutorPanelComponent,
    Wk8SorterenComponent,
    Wk8WisselklankenComponent,
    Wk8WoordpadComponent,
    DustParticlesComponent,
    Wk8LeesComponent,
  ],
  templateUrl: './wk8-level-activity.component.html',
  styleUrl: './wk8-level-activity.component.css',
  animations: [MODAL_ANIMATION(false), FADE_ANIMATION(false), SLIDE_ANIMATION('left'), CAROUSEL_ANIMATION(250)],
})
export class Wk8LevelActivityComponent implements OnInit {
  readonly #wk8AudioService = inject(Wk8AudioService);
  readonly #wk8WordService = inject(Wk8WordService);
  readonly #lpActionsService = inject(LpActionsService);
  readonly #lpMissionService = inject(LpMissionService);
  readonly #adaptivityService = inject(AdaptivityService);
  readonly #wk8BookService = inject(Wk8BookService);
  readonly #wk8GlobalsService = inject(Wk8GlobalsService);
  readonly #wk8ReadService = inject(Wk8ReadService);
  readonly _wk8TutorService = inject(Wk8TutorService);
  readonly _stylesService = inject(StylesService);
  readonly scaleDefaultWidthPx = 1000;

  activity = input.required<LpWoordKr8LevelActivity>();
  mechanicPresetFlowList = computed<MechanicPreset[]>(() => {
    const mechanicsString = this.activity().mechanics;
    const mechanics = mechanicsString.split(',').map((m) => m.trim());
    if (mechanicsString.length === 0 || mechanics.length === 0)
      return ['wissel_klanken', 'sorteer_woorden', 'woordpad', 'lees_zin', 'lees_woord', 'lees_flitsen', 'lees_flitsenMutaties'];

    const invalid = mechanics.filter((m) => !MECHANIC_PRESETS.includes(m as any));
    const valid = mechanics.filter((m) => MECHANIC_PRESETS.includes(m as any)) as MechanicPreset[];

    if (invalid.length > 0) {
      console.warn(`Invalid mechanics in activity: ${invalid.join(', ')}`);
    }

    return valid;
  });
  #words = signal<LpWoordData[]>([]);
  sentence = signal<LpWk8BookSentence | undefined>(undefined);
  sentencePart = signal<LpWk8BookSentencePartFilter | undefined>(undefined);
  index = signal(0);
  fakeLoading = signal(false);
  viewportSize = signal({ width: window.innerWidth, height: window.innerHeight });

  mechanicElement = viewChild<ElementRef<HTMLDivElement>>('mechanic');

  @HostListener('window:resize')
  onResize() {
    this.viewportSize.set({ width: window.innerWidth, height: window.innerHeight });
  }
  currentScaleMultiplier = computed(() => {
    this.viewportSize();
    const element = this.mechanicElement()?.nativeElement;
    if (!element) {
      return 1;
    }
    const containerWidth = element.offsetWidth;
    return containerWidth / this.scaleDefaultWidthPx;
  });

  currentMechanicWidth = computed(() => {
    const element = this.mechanicElement()?.nativeElement;
    if (!element) {
      return 0;
    }
    return element.offsetWidth;
  });

  currentWord = computed(() => {
    const idx = this.index();
    return this.#words()[idx % this.#words().length];
  });

  currentMechanicPreset = computed(() => {
    const idx = Math.floor(this.index() / this.#words().length);
    if (idx >= this.mechanicPresetFlowList().length) return undefined;

    return this.mechanicPresetFlowList()[idx];
  });

  currentMechanicInput = computed(() => {
    const currentWord = this.currentWord();
    const currentMechanicPreset = this.currentMechanicPreset();
    if (!currentWord || !currentMechanicPreset) return undefined;
    return this.getMechanicInput(currentWord, currentMechanicPreset);
  });

  donePlaying = computed(() => this.sentencePart() === undefined);
  isBuildingSentence = computed(() => this.index() >= this.#words().length * this.mechanicPresetFlowList().length);

  private async loadLevel(sentence: LpWk8BookSentence, part: LpWk8BookSentencePartFilter) {
    this.sentence.set(sentence);
    await this.loadSentencePart(part);
  }

  private async loadSentencePart(part: LpWk8BookSentencePartFilter) {
    this.sentencePart.set(part);
    this.#words.set(await this.#wk8BookService.getOpenSentencePartOptions(part));
    this.index.set(0);
  }

  async ngOnInit() {
    await this.#wk8BookService.waitForLoad();
    await this.#wk8WordService.waitForLoad();

    const currentLevel = this.#wk8GlobalsService.currentLevel;
    if (!currentLevel) {
      throw new Error('No current level set in Wk8GlobalsService');
    }

    await this.loadLevel(currentLevel.sentence, currentLevel.part);

    if (this.#words().length === 0) {
      this.#words.set(this.#wk8WordService.getNumWordsAdaptively(3));
    }
    this.index.set(0);
  }

  async finish() {
    await this.#lpMissionService.finishActivity(this.activity());
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.goToNextMechanic();
    }
  }

  async goToNextMechanic() {
    const currentMechanic = this.currentMechanicPreset();
    this.#wk8AudioService.playNextActivity();

    this.index.update((index) => index + 1);
    const nextMechanic = this.currentMechanicPreset();

    if (currentMechanic !== nextMechanic) {
      this._wk8TutorService.clear();
    }

    while (!this.isBuildingSentence()) {
      const input = this.currentMechanicInput();
      if (input) {
        break; // If we have a valid mechanic input, we can break the loop
      }

      this.index.update((index) => index + 1);
    }

    //fake loading for 1ms to flush repeating components
    this.fakeLoading.set(true);
    await waitForMillis(1);
    this.fakeLoading.set(false);
  }

  async filledVariable() {
    await this.#lpActionsService.perform(this.activity().onWordFilled);

    const isCompleted = this.#wk8BookService.isBookComplete(await this.#wk8BookService.getBookForSentence(this.sentence()!));
    if (isCompleted) {
      await this.#lpActionsService.perform(this.activity().onBookCompleted);
    }

    this.sentencePart.set(undefined);
    this.finish();
  }

  getPart(index: number) {
    const parts = this.sentence()!.parts;
    for (let i = index; i < parts.length; i++) {
      const part = parts[i];
      if (part.dataType !== 'Wk8BookSentencePartFilter') continue;

      return part;
    }
    return undefined;
  }

  getMechanicInput(targetWord: LpWoordData, mechanicPreset: MechanicPreset): MechanicInput | undefined {
    switch (mechanicPreset) {
      case 'lees_flitsen':
        return this.createLeesLijstInput(targetWord);
      case 'lees_flitsenMutaties':
        return this.createLeesPadInput(targetWord);
      case 'lees_woord':
        return {
          componentSelector: 'wk8-lees',
          arguments: {
            variant: new LeesFlitsenVariant(
              [
                WordToKlankConverter.breakupWord(targetWord.word), //
              ],
              false,
              {
                wk8ReadService: this.#wk8ReadService,
                wk8TutorService: this._wk8TutorService,
              },
            ),
          },
        } satisfies LezenInput;
      case 'wissel_klanken':
        return {
          componentSelector: 'wk8-wisselklanken',
          arguments: {
            targetWord: targetWord,
          },
        } satisfies WisselInput;
      case 'sorteer_woorden':
        return this.createSorteerWoordenInput(targetWord);
      case 'woordpad': {
        return this.createWoordpadInput(targetWord);
      }
      case 'lees_zin':
        const [sentence] = sampleRandom(targetWord.sentences?.split(';'), 1);
        if (!sentence) return undefined;

        return {
          componentSelector: 'wk8-lees',
          arguments: {
            variant: new LeesZinVariant(sentence.split(' '), {
              wk8ReadService: this.#wk8ReadService,
              wk8TutorService: this._wk8TutorService,
            }),
          },
        } satisfies LezenInput;

      default:
        throw new Error(`Unknown mechanic preset: ${mechanicPreset}`);
    }
  }

  createSorteerWoordenInput(targetWord: LpWoordData): SorteerInput {
    const mapWordToMediaResource = (woordData: LpWoordData): MediaResource => ({
      label: woordData.word,
      image_url: woordData.image?.url,
      audio_url: woordData.pronunciation1?.url,
    });
    const mapWordToQuizOption = (woordData: LpWoordData, correct: boolean): QuizOption => ({
      ...mapWordToMediaResource(woordData),
      correct: correct,
    });

    const numOtherOptions = 2;

    // Select other options: prefer similar words, fill with adaptive if needed
    const similarWords = this.#wk8WordService.findSimilarWords(targetWord.word, 1, 'FILTERED');
    //map back to worddata
    const similarWordData = similarWords
      .map((word) => this.#wk8WordService.findWord(word, 'FILTERED'))
      .filter((word) => word !== undefined);
    // do adaptiveChoice within the similarwords
    const adaptiveSimilarWords = this.#adaptivityService.adaptiveChoice(similarWordData, numOtherOptions);

    // If less than needed, fill with adaptive words (excluding already selected and target)
    if (adaptiveSimilarWords.length < numOtherOptions) {
      const needed = numOtherOptions - adaptiveSimilarWords.length;
      const excludeUids = [targetWord.uid, ...adaptiveSimilarWords.map((w) => w.uid)];
      this.#adaptivityService
        .adaptiveChoice(
          this.#wk8WordService.words().filter((w) => !excludeUids.includes(w.uid)),
          needed,
        )
        .forEach((w) => adaptiveSimilarWords.push(w));
    }

    const options = [mapWordToQuizOption(targetWord, true), ...adaptiveSimilarWords.map((w) => mapWordToQuizOption(w, false))];

    return {
      componentSelector: 'wk8-sorteren',
      arguments: {
        question: mapWordToMediaResource(targetWord),
        options: options,
      },
    };
  }

  createWoordpadInput(targetWord: LpWoordData): WoordpadInput | undefined {
    const randomMutationPath = this.#wk8WordService.generateRandomMutationPath(targetWord.word, 4);
    //find the lpworddata in wordList matching the randomPath
    const wordDataPath: LpWoordData[] = [];
    for (const word of randomMutationPath) {
      const foundWord = this.#wk8WordService.words().find((w) => w.word === word.word);
      if (foundWord) {
        wordDataPath.push(foundWord);
      }
    }
    if (wordDataPath.length <= 1) return undefined; // no path found, skip this mechanic

    return {
      componentSelector: 'wk8-woordpad',
      arguments: {
        path: wordDataPath,
      },
    };
  }

  createLeesLijstInput(targetWord: LpWoordData): LezenInput {
    const numExtraWords = 4;
    const otherWords = this.#adaptivityService.adaptiveChoice(this.#wk8WordService.words(), numExtraWords);

    //add the target word at the end
    otherWords.push(targetWord);
    return {
      componentSelector: 'wk8-lees',
      arguments: {
        variant: new LeesFlitsenVariant(
          otherWords.map((w) => WordToKlankConverter.breakupWord(w.word)),
          false,
          {
            wk8ReadService: this.#wk8ReadService,
            wk8TutorService: this._wk8TutorService,
          },
        ),
      },
    };
  }

  createLeesPadInput(targetWord: LpWoordData): LezenInput | undefined {
    const randomMutationPath = this.#wk8WordService.generateRandomMutationPath(targetWord.word, 4);
    if (randomMutationPath.length <= 1) return undefined;

    return {
      componentSelector: 'wk8-lees',
      arguments: {
        variant: new LeesFlitsenVariant(
          randomMutationPath.map((word) => WordToKlankConverter.breakupWord(word.word)),
          true,
          {
            wk8ReadService: this.#wk8ReadService,
            wk8TutorService: this._wk8TutorService,
          },
        ),
      },
    };
  }

  getRandomMutationPath(targetWord: LpWoordData, allWords: LpWoordData[], maxPathLength: number): string[] {
    const wordStringList = allWords.map((wordData) => wordData.word);
    const treeBuilder = new MutationTreeBuilder(wordStringList);
    return treeBuilder.getRandomMutationPath(targetWord.word, maxPathLength);
  }
}
